/**
 * ABAC permission gates for UI affordances.
 *
 * Per PTV2-915 the rule is "hide, do not disable" — if the user lacks the
 * permission they never see the affordance. The server still enforces the
 * decision; this module only decides what the UI shows.
 *
 * The `RULES` map below is the single source of truth: every gate adds one
 * entry here and is referenced everywhere by its semantic key (e.g.
 * `'createPatients'`). Resource/action string literals MUST NOT appear at
 * call sites — that's how we drifted from the BFF contract last time
 * (plural vs singular nouns).
 */

import type { AuthUser } from './types';

/**
 * BFF `/me` resource vocabulary — lowercase singular nouns
 * (`PTV2-907-me-api.md`). Extend this union as the contract grows; the
 * `RULES` table below is type-checked against it.
 */
type Resource =
  | 'patient'
  | 'family'
  | 'phenotype'
  | 'diagnosis'
  | 'variant'
  | 'admin'
  | 'pedigree'
  | 'search'
  | 'platform';

/** Actions that satisfy a gate — first match wins in `can()`. */
interface Rule {
  readonly resource: Resource;
  readonly actions: readonly string[];
}

const RULES = {
  browsePatients: { resource: 'patient', actions: ['READ'] },
  /** BFF used `WRITE`; live services emit `CREATE` / `UPDATE` / `MANAGE`. */
  createPatients: {
    resource: 'patient',
    actions: ['WRITE', 'CREATE', 'UPDATE', 'MANAGE'],
  },
  /** Care-team membership writes (set primary, add/remove, role edit) — PTV2-1095. */
  editCareTeam: {
    resource: 'patient',
    actions: ['WRITE', 'UPDATE', 'MANAGE'],
  },
  createFamilies: {
    resource: 'family',
    actions: ['WRITE', 'CREATE', 'UPDATE', 'MANAGE'],
  },
  openAdmin: {
    resource: 'admin',
    actions: ['READ', 'MANAGE', 'UPDATE', 'WRITE', 'CREATE'],
  },
  /**
   * Open the embedded pedigree editor (PTV2-1157). READ suffices to view; the editor's writes are
   * enforced per-endpoint by the backend `@PreCheckPermission` on the pedigree resource.
   */
  openPedigree: {
    resource: 'pedigree',
    actions: ['READ', 'UPDATE', 'WRITE', 'CREATE', 'MANAGE'],
  },
  /**
   * Elevated config management — writing a sensitive config key (secret /
   * security / password) or re-encrypting secrets requires `admin:MANAGE`
   * (PTV2-1067 D15/D18). Latent under the default allow-all-ADMIN policy until a
   * deployment grants MANAGE to a narrower set than UPDATE.
   */
  manageConfig: { resource: 'admin', actions: ['MANAGE'] },
  /**
   * PTV2-1021 global plane. Global roles surface through `/api/v1/me` as
   * `platform:*`: PLATFORM_ADMIN → MANAGE, TENANT_OPERATOR → UPDATE,
   * VIEWER → READ. `openPlatform` gates the entire Platform section (any tier,
   * first match wins). A tenant ADMIN (`admin:*` but no `platform:*`) is
   * deliberately NOT sufficient — the two planes are distinct.
   */
  openPlatform: { resource: 'platform', actions: ['READ', 'UPDATE', 'MANAGE'] },
  /**
   * Elevated platform power (PLATFORM_ADMIN). Gates the Tenant templates and
   * Cluster grants tabs plus destructive affordances. Per the contract,
   * `cluster_user_tenant_grant` filtering is server-side only — the UI just
   * needs the tier.
   */
  managePlatform: { resource: 'platform', actions: ['MANAGE'] },
} as const satisfies Record<string, Rule>;

export type PermissionKey = keyof typeof RULES;

/** Returns `true` iff the user is granted the named gate. */
export function can(user: AuthUser | null, key: PermissionKey): boolean {
  if (user === null) return false;
  const { resource, actions } = RULES[key];
  for (const action of actions) {
    if (user.permissions.includes(`${resource}:${action}`)) return true;
  }
  return false;
}

/**
 * Whether the authenticated user has ANY effective access to the application.
 *
 * A user can now be provisioned with zero roles (JIT with no default roles, or
 * every claim role unmapped-and-dropped — PTV2-1413 / contract §4.1). The
 * backend `/me` derives permissions from the JWT authorities, so an empty role
 * set surfaces here as an empty `permissions` list. When that is the case the
 * app must show the branded "no permissions" dead-end rather than the normal
 * shell (which would only render empty screens and fire `view`-denied queries).
 *
 * This is purely additive — it never widens or bypasses any `can()` gate.
 */
export function hasAnyPermission(user: AuthUser | null): boolean {
  return user !== null && user.permissions.length > 0;
}
